import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  Snackbar,
  makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  Close,
  FileCopyOutlined,
  CheckCircle as CheckCircleIcon,
  CalendarToday,
  Update,
  Person,
  Business,
  Fingerprint,
  Label as LabelIcon,
} from "@material-ui/icons";

import { useTranslation } from "../hooks/useTranslation";

/* Largura padrão do drawer. Suficiente para mostrar listas de arquivos
   completas sem comprimir e para acomodar pares rótulo/valor mais longos
   (ex.: "Profundidade da Fundamentação" + escala + rótulo do nível) sem
   quebrar de linha; ainda deixa parte da tabela visível atrás. */
const DRAWER_WIDTH = 640;

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: DRAWER_WIDTH,
    maxWidth: "92vw",
    backgroundColor: "var(--ikaro-bg-surface)",
    color: "var(--ikaro-text-primary)",
    display: "flex",
    flexDirection: "column",
    boxShadow: "var(--ikaro-shadow-heavy)",
  },
  /* Cabeçalho clean: fundo da própria superfície, com um filete da marca
     no topo (gradiente laranja→roxo) para identidade visual sem competir
     com o conteúdo. Mantém um título grande com o nome da tarefa, kicker
     pequeno em maiúsculas e o ID em monoespaçado com ação de copiar. */
  header: {
    flexShrink: 0,
    position: "relative",
    backgroundColor: "var(--ikaro-bg-surface)",
    borderBottom: "1px solid var(--ikaro-border-default)",
    padding: theme.spacing(2, 2.5),
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: "var(--ikaro-gradient-banner)",
    },
  },
  headerInfo: {
    minWidth: 0,
    flex: 1,
  },
  headerKicker: {
    fontSize: "0.65rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.7px",
    color: "var(--ikaro-orange-primary)",
  },
  headerTitle: {
    fontSize: "1.05rem",
    fontWeight: 700,
    lineHeight: 1.3,
    marginTop: 4,
    color: "var(--ikaro-text-primary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    wordBreak: "break-word",
  },
  headerIdRow: {
    marginTop: theme.spacing(1),
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontSize: "0.72rem",
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    color: "var(--ikaro-text-secondary)",
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
    border: "1px solid var(--ikaro-border-default)",
    padding: theme.spacing(0.25, 0.75),
    borderRadius: theme.spacing(0.5),
  },
  headerCopyBtn: {
    color: "var(--ikaro-text-muted)",
    padding: 2,
    "&:hover": {
      color: "var(--ikaro-orange-primary)",
      backgroundColor: "var(--ikaro-orange-light)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "0.95rem",
    },
  },
  closeBtn: {
    color: "var(--ikaro-text-secondary)",
    padding: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: "var(--ikaro-bg-surface-hover)",
      color: "var(--ikaro-text-primary)",
    },
  },
  body: {
    flex: 1,
    overflow: "auto",
    padding: theme.spacing(2, 2.5, 3),
  },
  /* Cada bloco de informação no corpo do drawer. Mantemos um título tipo
     "etiqueta" em caixa alta + filete colorido para escanear rapidamente. */
  section: {
    marginBottom: theme.spacing(2.5),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  sectionAccent: {
    width: 3,
    height: 18,
    borderRadius: 2,
    backgroundColor: "var(--ikaro-orange-primary)",
  },
  sectionAccentSecondary: {
    backgroundColor: "var(--ikaro-purple-accent)",
  },
  sectionAccentNeutral: {
    backgroundColor: "var(--ikaro-gray-medium)",
  },
  sectionTitle: {
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    color: "var(--ikaro-text-secondary)",
  },
  sectionBody: {
    paddingLeft: theme.spacing(1.5),
  },
  /* Grade de pares "rótulo / valor". Usa CSS grid para alinhar verticalmente
     mesmo quando os valores têm tamanhos muito diferentes. */
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(0.75),
    alignItems: "start",
  },
  metaLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    fontSize: "0.75rem",
    color: "var(--ikaro-text-secondary)",
    fontWeight: 500,
    "& .MuiSvgIcon-root": {
      fontSize: "1rem",
      color: "var(--ikaro-text-muted)",
    },
  },
  metaValue: {
    fontSize: "0.82rem",
    color: "var(--ikaro-text-primary)",
    fontWeight: 500,
    wordBreak: "break-word",
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  metaValueMuted: {
    color: "var(--ikaro-text-muted)",
    fontStyle: "italic",
    fontWeight: 400,
  },
  monoChip: {
    height: 22,
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: "0.72rem",
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
    border: "1px solid var(--ikaro-border-default)",
    color: "var(--ikaro-text-primary)",
    "& .MuiChip-label": {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0.75),
    },
  },
  copyInlineBtn: {
    padding: 2,
    color: "var(--ikaro-text-muted)",
    "&:hover": {
      color: "var(--ikaro-orange-primary)",
      backgroundColor: "var(--ikaro-orange-light)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "0.9rem",
    },
  },
  divider: {
    margin: theme.spacing(2, 0),
    backgroundColor: "var(--ikaro-border-light)",
  },
}));

/* Copia para a área de transferência usando a API moderna quando disponível.
   Fallback para `document.execCommand("copy")` para suportar contextos sem
   `navigator.clipboard` (ex.: páginas servidas em http durante dev). */
const copyToClipboard = async (text) => {
  if (text == null) return false;
  const value = String(text);
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch (_err) {
    /* cai para fallback abaixo */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_err) {
    return false;
  }
};

/* `MetaRow` é um par rótulo/valor com ícone opcional e botão de copiar.
   Devolve `null` quando não há valor para evitar linhas vazias na grade. */
export const MetaRow = ({
  label,
  icon,
  value,
  copyValue,
  monospace = false,
  emptyText,
}) => {
  const classes = useStyles();
  const { t: tCommon } = useTranslation("common");
  const [copied, setCopied] = useState(false);

  const hasValue =
    value !== null &&
    value !== undefined &&
    !(typeof value === "string" && value.trim() === "");

  const handleCopy = async () => {
    const ok = await copyToClipboard(copyValue ?? value);
    if (ok) {
      setCopied(true);
    }
  };

  if (!hasValue && !emptyText) return null;

  return (
    <>
      <Box component="span" className={classes.metaLabel}>
        {icon}
        {label}
      </Box>
      <Box
        className={`${classes.metaValue} ${
          !hasValue ? classes.metaValueMuted : ""
        }`}
      >
        {hasValue ? (
          monospace ? (
            <Chip
              size="small"
              label={value}
              className={classes.monoChip}
            />
          ) : (
            <span>{value}</span>
          )
        ) : (
          <span>{emptyText}</span>
        )}
        {hasValue && copyValue !== null && (
          <Tooltip
            title={copied ? tCommon("copied") : tCommon("copy")}
            arrow
            placement="top"
            onClose={() => setTimeout(() => setCopied(false), 200)}
          >
            <IconButton
              size="small"
              className={classes.copyInlineBtn}
              onClick={handleCopy}
              aria-label={tCommon("copy")}
            >
              {copied ? <CheckCircleIcon /> : <FileCopyOutlined />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </>
  );
};

/* `Section` agrupa um conjunto de informações relacionadas dentro do drawer.
   `accent` controla a cor do filete ao lado do título: "primary" (laranja),
   "secondary" (roxo) ou "neutral" (cinza). */
export const Section = ({ title, accent = "primary", children }) => {
  const classes = useStyles();
  const accentClass =
    accent === "secondary"
      ? `${classes.sectionAccent} ${classes.sectionAccentSecondary}`
      : accent === "neutral"
        ? `${classes.sectionAccent} ${classes.sectionAccentNeutral}`
        : classes.sectionAccent;
  return (
    <Box className={classes.section}>
      <Box className={classes.sectionHeader}>
        <span className={accentClass} />
        <Typography component="h3" className={classes.sectionTitle}>
          {title}
        </Typography>
      </Box>
      <Box className={classes.sectionBody}>{children}</Box>
    </Box>
  );
};

/* Renderiza um conjunto de pares rótulo/valor em uma grade compacta. Pode
   receber qualquer combinação de `MetaRow` como filhos. */
export const MetaGrid = ({ children }) => {
  const classes = useStyles();
  return <Box className={classes.metaGrid}>{children}</Box>;
};

/* Bloco "padrão" de informações gerais do job. As páginas podem usar este
   bloco e/ou compor outras seções específicas (ex.: dados de entrada). */
export const JobMetaSection = ({ job }) => {
  const { t, formatDateTime } = useTranslation("jobs");
  const classes = useStyles();
  if (!job) return null;
  return (
    <Section title={t("drawer.sections.general")} accent="neutral">
      <MetaGrid>
        <MetaRow
          icon={<Fingerprint />}
          label={t("drawer.fields.jobId")}
          value={job.id != null ? String(job.id) : null}
          monospace
        />
        <MetaRow
          icon={<LabelIcon />}
          label={t("drawer.fields.externalId")}
          value={job.external_id || null}
          copyValue={job.external_id || null}
        />
        <MetaRow
          icon={<Business />}
          label={t("drawer.fields.cell")}
          value={job.cell_name || null}
          copyValue={null}
        />
        <MetaRow
          icon={<Person />}
          label={t("drawer.fields.user")}
          value={job.user_name || null}
          copyValue={null}
        />
        <MetaRow
          icon={<CalendarToday />}
          label={t("drawer.fields.createdAt")}
          value={formatDateTime(job.insertion_timestamp, {
            second: "2-digit",
          })}
          copyValue={null}
        />
        <MetaRow
          icon={<Update />}
          label={t("drawer.fields.updatedAt")}
          value={formatDateTime(job.update_timestamp, { second: "2-digit" })}
          copyValue={null}
        />
      </MetaGrid>
      <Divider className={classes.divider} />
    </Section>
  );
};

/* `JobDetailsDrawer` é o contêiner do painel lateral. Cada página define o
   conteúdo via `renderContent(job)` (ou `children`) — assim mantém o layout
   consistente sem acoplar regras específicas de cada tipo de tarefa. */
const JobDetailsDrawer = ({
  open,
  onClose,
  job,
  title,
  renderContent,
  children,
}) => {
  const classes = useStyles();
  const { t } = useTranslation("jobs");
  const { t: tCommon } = useTranslation("common");
  const [copied, setCopied] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleCopyId = async () => {
    if (!job?.id) return;
    const ok = await copyToClipboard(job.id);
    if (ok) {
      setCopied(true);
      setSnackbarOpen(true);
    }
  };

  const resolvedTitle =
    title || job?.job_name || job?.task || t("drawer.defaultTitle");

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
    >
      {job && (
        <>
          <Box className={classes.header}>
            <Box className={classes.headerInfo}>
              <Typography className={classes.headerKicker}>
                {t("drawer.kicker")}
              </Typography>
              <Typography className={classes.headerTitle}>
                {resolvedTitle}
              </Typography>
              {job?.id != null && (
                <Box className={classes.headerIdRow}>
                  <span>#{job.id}</span>
                  <Tooltip
                    title={copied ? tCommon("copied") : tCommon("copy")}
                    arrow
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      className={classes.headerCopyBtn}
                      onClick={handleCopyId}
                      aria-label={tCommon("copy")}
                    >
                      {copied ? (
                        <CheckCircleIcon />
                      ) : (
                        <FileCopyOutlined />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
            <IconButton
              size="small"
              className={classes.closeBtn}
              onClick={onClose}
              aria-label={tCommon("close")}
            >
              <Close />
            </IconButton>
          </Box>
          <Box className={classes.body}>
            {renderContent ? renderContent(job) : children}
          </Box>
        </>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => {
          setSnackbarOpen(false);
          setCopied(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" elevation={6} variant="filled">
          {tCommon("copiedToClipboard")}
        </Alert>
      </Snackbar>
    </Drawer>
  );
};

export default JobDetailsDrawer;
